import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, CreditCard, Banknote, FileText, Tag, Upload, X, Truck } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { fmt, proxyImg } from "../lib/format";

export default function Checkout() {
  const { items, clear } = useCart();
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [payment, setPayment] = useState("pix");
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [preview, setPreview] = useState(null);
  const [address, setAddress] = useState({
    full_name: user?.name || "",
    cep: "",
    street: "",
    number: "",
    complement: "",
    neighborhood: "",
    city: "",
    state: "SP",
    phone: "",
  });
  const [cepLoading, setCepLoading] = useState(false);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [shippingMethod, setShippingMethod] = useState(null);
  const [prescription, setPrescription] = useState(null);
  const [order, setOrder] = useState(null);
  const [processing, setProcessing] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (!authLoading && !user) nav("/login?next=/checkout");
  }, [user, authLoading, nav]);

  useEffect(() => {
    if (items.length === 0) return;
    api
      .post(`/cart/preview${appliedCoupon ? `?coupon_code=${appliedCoupon}` : ""}`, {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
      })
      .then((r) => setPreview(r.data));
  }, [items, appliedCoupon]);

  // Auto-fill address when CEP has 8 digits
  const handleCepChange = async (cep) => {
    const cleaned = cep.replace(/\D/g, "").slice(0, 8);
    setAddress((a) => ({ ...a, cep: cleaned }));
    if (cleaned.length === 8) {
      setCepLoading(true);
      try {
        const { data } = await api.get(`/cep/${cleaned}`);
        setAddress((a) => ({
          ...a,
          street: data.street || a.street,
          neighborhood: data.neighborhood || a.neighborhood,
          city: data.city || a.city,
          state: data.state || a.state,
        }));
        // Calculate shipping
        const { data: ship } = await api.post("/shipping/calculate", {
          cep: cleaned,
          state: data.state,
          weight: 0.5 * items.reduce((s, i) => s + i.quantity, 0),
        });
        setShippingOptions(ship.options);
        setShippingMethod(ship.options[0]);
      } catch (e) {
        // ignore
      } finally {
        setCepLoading(false);
      }
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) {
      alert("Arquivo muito grande (máx. 3MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPrescription({ filename: f.name, base64: reader.result, size: f.size, type: f.type });
    };
    reader.readAsDataURL(f);
  };

  const applyCoupon = () => coupon && setAppliedCoupon(coupon.toUpperCase());

  const subtotalAfterCoupon = preview?.total || 0;
  const pixDiscount = payment === "pix" ? Math.round(subtotalAfterCoupon * 0.05 * 100) / 100 : 0;
  const shippingCost = shippingMethod?.price || 0;
  const finalTotal = Math.max(subtotalAfterCoupon - pixDiscount + shippingCost, 0);

  const placeOrder = async () => {
    setProcessing(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        address,
        payment_method: payment,
        coupon_code: appliedCoupon || undefined,
        prescription_base64: prescription?.base64 || undefined,
        prescription_filename: prescription?.filename || undefined,
        shipping_method: shippingMethod?.id || "PAC",
        shipping_cost: shippingCost,
      });
      // Persist a hint locally so /checkout/result can resume polling even if MP loses query params
      try {
        localStorage.setItem("biofarmax:last_order_id", data.order.order_id);
      } catch {}
      // Clear cart now (order is already created on backend; user goes to MP)
      clear();
      const initPoint = data?.payment?.init_point;
      if (initPoint && !data?.payment?.is_mocked) {
        window.location.href = initPoint;
        return;
      }
      // Fallback for mocked environment
      await api.post(`/orders/${data.order.order_id}/confirm-payment`).catch(() => {});
      setOrder(data.order);
    } catch (e) {
      alert(e.response?.data?.detail || "Erro ao processar pedido");
    } finally {
      setProcessing(false);
    }
  };

  if (items.length === 0 && !order) {
    return (
      <div className="pt-32 pb-24 max-w-2xl mx-auto px-5 text-center">
        <h1 className="h-display text-4xl mb-4">Seu carrinho está vazio</h1>
        <Link to="/loja" className="btn-primary">Ver produtos</Link>
      </div>
    );
  }

  if (order) {
    return (
      <div className="pt-32 pb-24 max-w-2xl mx-auto px-5" data-testid="checkout-success">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-status-success/15 grid place-items-center">
            <Check size={42} className="text-status-success" strokeWidth={2.5} />
          </div>
          <div className="eyebrow mb-3">Pedido confirmado</div>
          <h1 className="h-display text-5xl mb-4">Obrigado, {user?.name?.split(" ")[0]}!</h1>
          <p className="text-ink-secondary mb-8 max-w-md mx-auto">
            Seu pedido <span className="font-tech text-brand-accent">{order.order_id}</span> foi recebido.
            <br />Total pago: <strong>{fmt(order.total)}</strong>
          </p>
          {order.prescription_id && (
            <div className="bg-brand-accent/10 border border-brand-accent/30 rounded-2xl p-4 mb-6 text-sm">
              📋 Receita médica anexada · será analisada pelo nosso farmacêutico
            </div>
          )}
          <div className="bg-bg-secondary rounded-3xl p-6 mb-8 text-left">
            <div className="text-xs uppercase tracking-widest font-tech text-ink-secondary mb-2">Pagamento</div>
            <div className="font-tech text-lg">{payment === "pix" ? "PIX (5% OFF aplicado)" : payment === "credit_card" ? "Cartão de Crédito" : "Boleto"}</div>
            <p className="text-xs text-ink-secondary mt-3">Pagamento confirmado via Mercado Pago.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/conta" className="btn-primary" data-testid="view-orders-btn">Meus pedidos</Link>
            <Link to="/loja" className="btn-outline">Continuar comprando</Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto px-5 md:px-10">
        <div className="mb-10">
          <div className="eyebrow mb-3">Finalização</div>
          <h1 className="h-display text-5xl">Checkout</h1>
        </div>

        <div className="grid lg:grid-cols-[1fr_400px] gap-10">
          <div className="space-y-6">
            {/* ADDRESS */}
            <section className="bg-white rounded-3xl p-6 md:p-8 border border-black/5">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-full bg-brand-primary text-white grid place-items-center font-tech text-sm">1</div>
                <h2 className="font-heading text-2xl">Endereço de entrega</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="addr-name" required placeholder="Nome completo" className="px-4 py-3 rounded-xl border border-black/10 sm:col-span-2" value={address.full_name} onChange={(e) => setAddress({ ...address, full_name: e.target.value })} />
                <div className="relative">
                  <input data-testid="addr-cep" required placeholder="CEP" className="px-4 py-3 rounded-xl border border-black/10 w-full" value={address.cep} onChange={(e) => handleCepChange(e.target.value)} maxLength={9} />
                  {cepLoading && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />}
                </div>
                <input data-testid="addr-phone" required placeholder="Telefone" className="px-4 py-3 rounded-xl border border-black/10" value={address.phone} onChange={(e) => setAddress({ ...address, phone: e.target.value })} />
                <input data-testid="addr-street" required placeholder="Rua" className="px-4 py-3 rounded-xl border border-black/10 sm:col-span-2" value={address.street} onChange={(e) => setAddress({ ...address, street: e.target.value })} />
                <input data-testid="addr-number" required placeholder="Número" className="px-4 py-3 rounded-xl border border-black/10" value={address.number} onChange={(e) => setAddress({ ...address, number: e.target.value })} />
                <input data-testid="addr-complement" placeholder="Complemento (opcional)" className="px-4 py-3 rounded-xl border border-black/10" value={address.complement} onChange={(e) => setAddress({ ...address, complement: e.target.value })} />
                <input data-testid="addr-neighborhood" required placeholder="Bairro" className="px-4 py-3 rounded-xl border border-black/10" value={address.neighborhood} onChange={(e) => setAddress({ ...address, neighborhood: e.target.value })} />
                <input data-testid="addr-city" required placeholder="Cidade" className="px-4 py-3 rounded-xl border border-black/10" value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} />
              </div>
            </section>

            {/* SHIPPING */}
            {shippingOptions.length > 0 && (
              <section className="bg-white rounded-3xl p-6 md:p-8 border border-black/5">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 rounded-full bg-brand-primary text-white grid place-items-center font-tech text-sm">2</div>
                  <h2 className="font-heading text-2xl">Frete e entrega</h2>
                </div>
                <div className="space-y-3">
                  {shippingOptions.map((opt) => (
                    <label
                      key={opt.id}
                      className={`flex items-center gap-4 p-4 rounded-2xl border-2 cursor-pointer transition ${
                        shippingMethod?.id === opt.id ? "border-brand-accent bg-brand-accent/5" : "border-black/10 hover:border-black/20"
                      }`}
                      data-testid={`ship-${opt.id}`}
                    >
                      <input type="radio" name="ship" checked={shippingMethod?.id === opt.id} onChange={() => setShippingMethod(opt)} className="sr-only" />
                      <Truck size={20} className="text-brand-primary" strokeWidth={1.5} />
                      <div className="flex-1">
                        <div className="font-heading text-lg">{opt.name}</div>
                        <div className="text-xs text-ink-secondary">{opt.company} · entrega em até {opt.delivery_days} dias</div>
                      </div>
                      <div className="font-tech text-lg text-brand-primary">{opt.price === 0 ? "Grátis" : fmt(opt.price)}</div>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-ink-secondary mt-4">Frete estimado — cálculo local (integração Melhor Envio disponível sob solicitação).</p>
              </section>
            )}

            {/* PRESCRIPTION */}
            <section className="bg-white rounded-3xl p-6 md:p-8 border border-black/5" data-testid="prescription-section">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-brand-primary text-white grid place-items-center font-tech text-sm">3</div>
                <h2 className="font-heading text-2xl">Receita médica</h2>
                <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-bg-tertiary text-ink-secondary uppercase tracking-widest font-tech">opcional</span>
              </div>
              <p className="text-sm text-ink-secondary mb-5">
                Para medicamentos manipulados sob prescrição, anexe sua receita. Aceitamos JPG, PNG ou PDF (máx. 3MB).
              </p>
              {prescription ? (
                <div className="flex items-center gap-3 p-3 bg-brand-accent/10 border border-brand-accent/30 rounded-2xl">
                  <FileText size={18} className="text-brand-accent" />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{prescription.filename}</div>
                    <div className="text-xs text-ink-secondary">{(prescription.size / 1024).toFixed(1)} KB</div>
                  </div>
                  <button onClick={() => setPrescription(null)} className="p-2 hover:bg-white rounded-full" data-testid="remove-prescription">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileRef.current?.click()}
                  className="w-full p-6 border-2 border-dashed border-black/15 rounded-2xl hover:border-brand-accent hover:bg-brand-accent/5 transition flex flex-col items-center gap-2"
                  data-testid="upload-prescription"
                >
                  <Upload size={24} className="text-brand-primary" strokeWidth={1.5} />
                  <span className="font-tech text-sm">Clique para enviar sua receita</span>
                  <span className="text-xs text-ink-secondary">PDF, JPG, PNG · até 3MB</span>
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,image/*,application/pdf"
                onChange={handleFileChange}
                className="hidden"
                data-testid="prescription-input"
              />
            </section>

            {/* PAYMENT */}
            <section className="bg-white rounded-3xl p-6 md:p-8 border border-black/5">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-full bg-brand-primary text-white grid place-items-center font-tech text-sm">4</div>
                <h2 className="font-heading text-2xl">Forma de pagamento</h2>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                {[
                  { id: "pix", Icon: Banknote, label: "PIX", sub: "5% OFF" },
                  { id: "credit_card", Icon: CreditCard, label: "Cartão", sub: "Até 6×" },
                  { id: "boleto", Icon: FileText, label: "Boleto", sub: "3 dias úteis" },
                ].map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setPayment(m.id)}
                    className={`text-left p-4 rounded-2xl border-2 transition ${
                      payment === m.id ? "border-brand-accent bg-brand-accent/5" : "border-black/10 hover:border-black/20"
                    }`}
                    data-testid={`pay-${m.id}`}
                  >
                    <m.Icon size={22} className="mb-3 text-brand-primary" strokeWidth={1.5} />
                    <div className="font-heading text-lg">{m.label}</div>
                    <div className="text-xs text-brand-accent font-tech">{m.sub}</div>
                  </button>
                ))}
              </div>
              <p className="text-xs text-ink-secondary mt-5">
                Pagamentos processados via Mercado Pago com criptografia e proteção contra fraudes.
              </p>
            </section>

            {/* COUPON */}
            <section className="bg-white rounded-3xl p-6 md:p-8 border border-black/5">
              <div className="flex items-center gap-3 mb-5">
                <Tag size={20} className="text-brand-accent" />
                <h2 className="font-heading text-2xl">Cupom de desconto</h2>
              </div>
              <div className="flex gap-3">
                <input
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                  placeholder="Código (ex: BIOFARMAX10)"
                  className="flex-1 px-4 py-3 rounded-full border border-black/10 focus:border-brand-primary focus:outline-none font-tech text-sm"
                  data-testid="coupon-input"
                />
                <button onClick={applyCoupon} className="btn-outline" data-testid="apply-coupon">Aplicar</button>
              </div>
              {preview?.coupon && (
                <div className="mt-3 text-sm text-status-success">
                  ✓ Cupom {preview.coupon.code} aplicado: -{fmt(preview.discount)}
                </div>
              )}
            </section>
          </div>

          {/* SUMMARY */}
          <aside className="space-y-4">
            <div className="bg-white rounded-3xl p-6 border border-black/5 lg:sticky lg:top-24">
              <h3 className="font-heading text-2xl mb-5">Resumo</h3>
              <div className="space-y-3 mb-5 max-h-72 overflow-y-auto">
                {items.map((i) => (
                  <div key={i.product_id} className="flex gap-3 text-sm">
                    <img src={proxyImg(i.image)} alt="" className="w-14 h-14 rounded-lg bg-bg-secondary p-1 object-contain" />
                    <div className="flex-1">
                      <div className="font-medium">{i.name}</div>
                      <div className="text-xs text-ink-secondary">Qtd: {i.quantity}</div>
                    </div>
                    <div className="font-tech text-sm">{fmt(i.price * i.quantity)}</div>
                  </div>
                ))}
              </div>
              <div className="border-t border-black/5 pt-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-ink-secondary">Subtotal</span><span>{fmt(preview?.subtotal || 0)}</span></div>
                {preview?.discount > 0 && (
                  <div className="flex justify-between text-status-success"><span>Cupom</span><span>−{fmt(preview.discount)}</span></div>
                )}
                {pixDiscount > 0 && (
                  <div className="flex justify-between text-status-success"><span>PIX 5% OFF</span><span>−{fmt(pixDiscount)}</span></div>
                )}
                {shippingMethod && (
                  <div className="flex justify-between"><span className="text-ink-secondary">Frete ({shippingMethod.name})</span><span>{shippingCost === 0 ? "Grátis" : fmt(shippingCost)}</span></div>
                )}
                <div className="flex justify-between pt-3 border-t border-black/5">
                  <span className="font-heading text-lg">Total</span>
                  <span className="font-heading text-3xl text-brand-primary" data-testid="checkout-total">{fmt(finalTotal)}</span>
                </div>
              </div>
              <button
                onClick={placeOrder}
                disabled={processing || !address.full_name || !address.cep || address.cep.length !== 8}
                className="btn-primary w-full mt-6 disabled:opacity-50"
                data-testid="place-order-btn"
              >
                {processing ? "Processando..." : "Finalizar pedido"}
              </button>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
